import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listMembers, getMembersForListCached, exportMembers, downloadTemplate, importMembers, bulkDeleteMembers, getMember } from '../../api/fpaMembers';
import { listEstates } from '../../api/estates';
import { listLinkedParcelRefs } from '../../api/parcels';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import ShareSheet from '../../components/common/ShareSheet';
import { buildMemberSharePayload } from '../../utils/memberShare';
import { resolveMemberCentroid } from '../../utils/memberParcelCentroid';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import { FixedSizeList } from 'react-window';

// Highlight matching text within a string
const HighlightMatch = ({ text, searchTerm }) => {
  if (!searchTerm || !text) return <>{text}</>;
  const idx = String(text).toLowerCase().indexOf(searchTerm.toLowerCase());
  if (idx === -1) return <>{text}</>;
  const str = String(text);
  return (
    <>
      {str.slice(0, idx)}
      <Box component="span" sx={{ bgcolor: 'warning.light', px: 0.5, borderRadius: 0.5, fontWeight: 'bold' }}>
        {str.slice(idx, idx + searchTerm.length)}
      </Box>
      {str.slice(idx + searchTerm.length)}
    </>
  );
};

// Memoized member row. With ~900 members, selecting a checkbox or typing in
// search previously re-rendered every mounted row; memoizing on primitive
// props means only the row whose state actually changed re-renders.

/** Member vs non-member, shown on every row rather than only the exceptions. */
const TypeChip = ({ member }) => (
  <Chip
    label={member.member_type === 'non_member' ? 'Non-member' : 'Member'}
    size="small"
    variant="outlined"
    color={member.member_type === 'non_member' ? 'default' : 'primary'}
    sx={{ height: 20, fontSize: '0.7rem' }}
  />
);
const MemberRow = React.memo(function MemberRow({
  member,
  isSelected,
  isExpanded,
  hasSearch,
  isMobile,
  matches,
  debouncedSearch,
  colSpan,
  onShare,
  onToggleSelect,
  onToggleExpand,
  onNavigate,
}) {
  return (
    <>
      <TableRow
        hover
        selected={isSelected}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
        onClick={() => {
          if (hasSearch && matches) onToggleExpand(member.id);
          else onNavigate(member.id);
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(e) => onToggleSelect(member.id, e)}
            onClick={(e) => e.stopPropagation()}
          />
        </TableCell>
        {hasSearch && (
          <TableCell padding="checkbox">
            {matches ? (
              <IconButton size="small" onClick={(e) => onToggleExpand(member.id, e)}>
                {isExpanded
                  ? <KeyboardArrowUpIcon fontSize="small" />
                  : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            ) : null}
          </TableCell>
        )}
        {!isMobile && <TableCell>{member.plot_number || '-'}</TableCell>}
        <TableCell>{member.street || '-'}</TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{member.name}</span>
            {/* Narrow screens have no room for a Type column, so the badge
                stays inline there — but now for members too, not just the
                exceptions. */}
            {isMobile && <TypeChip member={member} />}
          </Box>
        </TableCell>
        {!isMobile && (
          <TableCell>
            <TypeChip member={member} />
          </TableCell>
        )}
        <TableCell align="center">
          {member.member_type === 'non_member' ? (
            <Typography variant="caption" color="text.secondary">—</Typography>
          ) : (
            <Chip
              label={member.current_year_paid ? 'Paid' : 'Unpaid'}
              color={member.current_year_paid ? 'success' : 'error'}
              size="small"
            />
          )}
        </TableCell>
        <TableCell padding="none" align="center">
          <Tooltip title="Share details">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onShare(member); }}
              aria-label={`Share ${member.name}`}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Expandable search match details */}
      {hasSearch && matches && (
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ py: 0 }}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Matched fields:
                </Typography>
                {matches.map((m, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{m.field}:</strong>{' '}
                    <HighlightMatch text={m.value} searchTerm={debouncedSearch} />
                  </Typography>
                ))}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  sx={{ mt: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigate(member.id);
                  }}
                >
                  View Full Details
                </Button>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
});

// ── Virtualized (no-search) rendering ───────────────────────────────── //
// When not searching, an open area renders its rows through react-window so
// even a 300+ member area only mounts the ~dozen visible rows. The search
// case keeps the MUI Table path above (few results, with expandable detail
// rows of variable height that don't fit a fixed-size list).


const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 44;
const MAX_LIST_HEIGHT = 480; // cap the in-area scroll viewport

// Mobile drops Plot and Street: at 360px there is no room for a Type
// column as well, so the type badge rides inline with the name there.
const gridCols = (isMobile) =>
  isMobile
    ? '40px minmax(0,1fr) 84px 40px'
    : '40px 110px minmax(0,1fr) minmax(0,1fr) 104px 104px 40px';

const MemberFlexRow = React.memo(function MemberFlexRow({
  member,
  isSelected,
  isMobile,
  onToggleSelect,
  onNavigate,
  onShare,
}) {
  return (
    <Box
      onClick={() => onNavigate(member.id)}
      sx={{
        display: 'grid',
        gridTemplateColumns: gridCols(isMobile),
        alignItems: 'center',
        gap: 1,
        px: 1,
        height: ROW_HEIGHT,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Checkbox
        size="small"
        checked={isSelected}
        onChange={(e) => onToggleSelect(member.id, e)}
        onClick={(e) => e.stopPropagation()}
      />
      {!isMobile && (
        <Typography variant="body2" noWrap>{member.plot_number || '-'}</Typography>
      )}
      {!isMobile && (
        <Typography variant="body2" noWrap>{member.street || '-'}</Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{member.name}</Typography>
        {isMobile && <TypeChip member={member} />}
      </Box>
      {!isMobile && (
        <Box sx={{ minWidth: 0 }}>
          <TypeChip member={member} />
        </Box>
      )}
      <Box sx={{ justifySelf: 'center' }}>
        {member.member_type === 'non_member' ? (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ) : (
          <Chip
            label={member.current_year_paid ? 'Paid' : 'Unpaid'}
            color={member.current_year_paid ? 'success' : 'error'}
            size="small"
          />
        )}
      </Box>
      <Tooltip title="Share details">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onShare(member); }}
          aria-label={`Share ${member.name}`}
        >
          <ShareIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

function VirtualMemberRow({ index, style, data }) {
  const { members, selectedIds, isMobile, onToggleSelect, onNavigate, onShare } = data;
  const member = members[index];
  return (
    <div style={style}>
      <MemberFlexRow
        member={member}
        isSelected={selectedIds.has(member.id)}
        isMobile={isMobile}
        onToggleSelect={onToggleSelect}
        onNavigate={onNavigate}
        onShare={onShare}
      />
    </div>
  );
}

function VirtualizedGroupBody({
  members,
  selectedIds,
  isMobile,
  currentYear,
  onToggleSelect,
  onNavigate,
  onShare,
  onToggleSelectAll,
}) {
  const allSelected = members.length > 0 && members.every((m) => selectedIds.has(m.id));
  const someSelected = members.some((m) => selectedIds.has(m.id));
  const listHeight = Math.min(members.length * ROW_HEIGHT, MAX_LIST_HEIGHT);
  const itemData = { members, selectedIds, isMobile, onToggleSelect, onNavigate, onShare };

  return (
    <Box sx={{ minWidth: 360 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridCols(isMobile),
          alignItems: 'center',
          gap: 1,
          px: 1,
          height: HEADER_HEIGHT,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Checkbox
          size="small"
          indeterminate={someSelected && !allSelected}
          checked={allSelected}
          onChange={(e) => onToggleSelectAll(members, e.target.checked)}
        />
        {!isMobile && <Typography variant="caption" fontWeight="bold">Plot #</Typography>}
        {!isMobile && <Typography variant="caption" fontWeight="bold">Street</Typography>}
        <Typography variant="caption" fontWeight="bold">Name</Typography>
        {!isMobile && <Typography variant="caption" fontWeight="bold">Type</Typography>}
        <Typography variant="caption" fontWeight="bold" sx={{ justifySelf: 'center' }}>
          Paid ({currentYear})
        </Typography>
        <span />
      </Box>
      <FixedSizeList
        height={listHeight}
        width="100%"
        itemCount={members.length}
        itemSize={ROW_HEIGHT}
        itemData={itemData}
        overscanCount={6}
      >
        {VirtualMemberRow}
      </FixedSizeList>
    </Box>
  );
}

const FPAMemberList = ({ embedded = false }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [members, setMembers] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [estates, setEstates] = useState([]);
  const [selectedEstate, setSelectedEstate] = useState(null);
  const [paidFilter, setPaidFilter] = useState('');
  // Both types by default. Non-members are still property records the FPA
  // knows about, so hiding them meant a search could confidently report
  // "no match" for someone who is in fact on file. 'all' is a real query
  // value, not an empty string — omitting the parameter selects members only.
  const [memberTypeFilter, setMemberTypeFilter] = useState('all');
  // Link-status filter: '' = all, 'linked', 'unlinked', 'conflict' (parcels
  // with >1 member). Only available with a single FPA selected (bulk mode).
  const [linkStatusFilter, setLinkStatusFilter] = useState('');
  const [memberIdToRef, setMemberIdToRef] = useState(() => new Map());
  const [conflictRefs, setConflictRefs] = useState(() => new Set());
  // Quick share. The list payload deliberately blanks contacts (the bulk
  // endpoint sends contacts: [] so a page of members doesn't ship everyone's
  // phone numbers), so the member is fetched on demand when the sheet opens.
  // Hydrating first and letting the sheet's buttons carry the gesture is also
  // what keeps navigator.share usable — awaiting a fetch inside the click
  // handler spends the transient user activation and iOS Safari rejects it.
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);

  const openShare = useCallback(async (member) => {
    setSharePayload(null);
    setShareError(null);
    setShareLoading(true);
    setShareOpen(true);
    try {
      const full = await getMember(member.id);
      // The list already knows this member's parcel ref (it drives the Link
      // status filter), so pass it in — that skips the links round-trip and
      // leaves only the single parcel fetch.
      const centroid = await resolveMemberCentroid(full, {
        parcelRef: memberIdToRef.get(member.id),
      });
      setSharePayload(buildMemberSharePayload(full, centroid));
    } catch (err) {
      console.error('Failed to load member for sharing:', err);
      setShareError('Could not load this member’s details. Please try again.');
    } finally {
      setShareLoading(false);
    }
  }, [memberIdToRef]);
  const [downloadAnchor, setDownloadAnchor] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [expandedMembers, setExpandedMembers] = useState({});

  // Bulk-select state — Set of selected member ids.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Import-tenant prompt state. Holds the staged file until the user
  // picks a tenant (or confirms the global filter's pre-selection).
  const [importDialog, setImportDialog] = useState(null); // { file, tenantId } | null
  const { tenants, tenantId: globalTenantId } = useTenant();
  const { can } = useAuth();
  const canCreateMember = can('fpa_member', 'create');
  const canImportMembers = can('fpa_member', 'import');
  const canExportMembers = can('fpa_member', 'export');

  const currentYear = new Date().getFullYear();

  // Debounce search input
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchInput]);

  // Fetch estates for filter
  useEffect(() => {
    const fetchEstates = async () => {
      try {
        const data = await listEstates({ page: 1, page_size: 100 });
        setEstates(data.estates || []);
      } catch (error) {
        console.error('Error fetching estates:', error);
      }
    };
    fetchEstates();
  }, []);

  // When a single tenant is selected we fetch the whole tenant once via the
  // version-cached bulk endpoint and filter client-side (instant, no refetch
  // per keystroke). With no tenant selected ("all tenants") the bulk-by-
  // tenant endpoint doesn't apply, so we fall back to the server list.
  const usingBulk = globalTenantId != null;

  // Fetch members — load all (no pagination)
  const fetchMembers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (usingBulk) {
        const data = await getMembersForListCached(globalTenantId);
        setMembers(data.members || []);
        return;
      }

      const params = { page: 1, page_size: 1000 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedEstate) params.estate_id = selectedEstate.id;
      if (paidFilter) {
        params.is_paid = paidFilter === 'paid' ? 'true' : 'false';
        params.paid_year = currentYear;
      }
      // Always sent: 'all' is what lifts the filter server-side. Leaving it
      // out falls back to the endpoint's members-only default.
      params.member_type = memberTypeFilter;

      const data = await listMembers(params);
      setMembers(data.members || []);
    } catch (error) {
      console.error('Error fetching FPA members:', error);
      setError('Failed to load FPA members. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [usingBulk, globalTenantId, debouncedSearch, selectedEstate, paidFilter, memberTypeFilter, currentYear]);

  // Fetch on filter change
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Parcel-link data for the "Link status" filter. Only meaningful with a
  // single FPA selected (links are keyed per tenant); cleared in all-FPAs mode.
  // Builds member→parcel_ref map and the set of refs with more than one member.
  useEffect(() => {
    if (globalTenantId == null) {
      setMemberIdToRef(new Map());
      setConflictRefs(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await listLinkedParcelRefs(globalTenantId);
        if (cancelled) return;
        const idToRef = new Map();
        const refCount = new Map();
        for (const link of data?.links || []) {
          idToRef.set(link.member_id, link.parcel_ref);
          refCount.set(link.parcel_ref, (refCount.get(link.parcel_ref) || 0) + 1);
        }
        const conflicts = new Set();
        for (const [ref, count] of refCount) if (count > 1) conflicts.add(ref);
        setMemberIdToRef(idToRef);
        setConflictRefs(conflicts);
      } catch (err) {
        if (!cancelled) console.warn('Failed to load parcel links for filter:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [globalTenantId]);

  // Group members by suburb + compute match info
  const { groupedMembers, matchInfo, visibleCount } = useMemo(() => {
    const searchLower = debouncedSearch.toLowerCase().trim();

    // In bulk mode the fetched list is the whole tenant, so apply the
    // search/estate/paid/type filters here. In server mode the response is
    // already filtered — render it as-is.
    const visible = usingBulk
      ? members.filter((m) => {
          if (memberTypeFilter !== 'all' && m.member_type !== memberTypeFilter) return false;
          if (selectedEstate && m.estate_id !== selectedEstate.id) return false;
          if (paidFilter === 'paid' && !m.current_year_paid) return false;
          if (paidFilter === 'unpaid' && m.current_year_paid) return false;
          if (linkStatusFilter === 'linked' && !memberIdToRef.has(m.id)) return false;
          if (linkStatusFilter === 'unlinked' && memberIdToRef.has(m.id)) return false;
          if (linkStatusFilter === 'conflict') {
            const ref = memberIdToRef.get(m.id);
            if (!ref || !conflictRefs.has(ref)) return false;
          }
          if (searchLower) {
            const hay = [m.ref_number, m.name, m.plot_number, m.street, m.area_suburb, m.estate_name, m.notes];
            if (!hay.some((v) => v && String(v).toLowerCase().includes(searchLower))) return false;
          }
          return true;
        })
      : members;

    // Determine which fields match the search for each member
    const matchMap = {};
    if (searchLower) {
      visible.forEach(m => {
        const matches = [];
        const fields = {
          'Ref #': m.ref_number,
          'Name': m.name,
          'Plot #': m.plot_number,
          'Street': m.street,
          'Area / Suburb': m.area_suburb,
          'Estate': m.estate_name,
          'Notes': m.notes,
        };
        Object.entries(fields).forEach(([label, value]) => {
          if (value && String(value).toLowerCase().includes(searchLower)) {
            matches.push({ field: label, value: String(value) });
          }
        });
        if (matches.length > 0) matchMap[m.id] = matches;
      });
    }

    // Group by area_suburb
    const groups = {};
    visible.forEach(m => {
      const suburb = m.area_suburb || 'Unknown';
      if (!groups[suburb]) groups[suburb] = [];
      groups[suburb].push(m);
    });

    // Sort suburbs alphabetically, "Unknown" last
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return a.localeCompare(b);
    });

    const sorted = sortedKeys.map(key => {
      // Sort members within each area by plot number (numeric if possible).
      const members = [...groups[key]].sort((a, b) => {
        const pa = a.plot_number ?? '';
        const pb = b.plot_number ?? '';
        const na = parseFloat(pa);
        const nb = parseFloat(pb);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return String(pa).localeCompare(String(pb), undefined, { numeric: true, sensitivity: 'base' });
      });
      return { suburb: key, members, count: members.length };
    });

    return { groupedMembers: sorted, matchInfo: matchMap, visibleCount: visible.length };
  }, [members, debouncedSearch, usingBulk, memberTypeFilter, selectedEstate, paidFilter, linkStatusFilter, memberIdToRef, conflictRefs]);

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
    setExpandedMembers({});
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setExpandedMembers({});
  };

  const handleCreateMember = () => {
    navigate('/members/fpa/create');
  };

  const toggleMemberExpand = useCallback((memberId, event) => {
    if (event) event.stopPropagation();
    setExpandedMembers(prev => ({ ...prev, [memberId]: !prev[memberId] }));
  }, []);

  // Stable navigate-to-member callback so memoized rows don't re-render.
  const goToMember = useCallback((memberId) => {
    navigate(`/members/fpa/${memberId}`);
  }, [navigate]);

  // Download menu
  const handleDownloadClick = (event) => {
    setDownloadAnchor(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchor(null);
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadData = async () => {
    handleDownloadClose();
    setIsExporting(true);
    setError(null);

    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedEstate) params.estate_id = selectedEstate.id;
      if (globalTenantId != null) params.tenant_id = globalTenantId;

      const response = await exportMembers(params);

      const today = new Date().toISOString().split('T')[0];
      triggerDownload(new Blob([response.data]), `fpa_members_${today}.xlsx`);
    } catch (error) {
      console.error('Error exporting members:', error);
      setError('Failed to export members. Please try again later.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    handleDownloadClose();
    setIsExporting(true);
    setError(null);

    try {
      const response = await downloadTemplate(
        globalTenantId != null ? { tenant_id: globalTenantId } : undefined,
      );
      triggerDownload(new Blob([response.data]), 'fpa_members_template.xlsx');
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template. Please try again later.');
    } finally {
      setIsExporting(false);
    }
  };

  // Import
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  // Stage the file and open the tenant-prompt dialog. The actual upload
  // happens in `handleImportConfirm` once the user picks a tenant. If
  // the global tenant filter is set, the dialog pre-selects (and locks)
  // it so the user just confirms.
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = '';
    setError(null);
    setSuccess(null);
    setImportResult(null);
    setImportDialog({ file, tenantId: globalTenantId || '' });
  };

  const handleImportConfirm = async () => {
    if (!importDialog?.file || !importDialog?.tenantId) return;
    const { file, tenantId: chosenTenantId } = importDialog;
    setImportDialog(null);
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenant_id', String(chosenTenantId));

      const result = await importMembers(formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(result);

      const changed = (result.created || 0) + (result.updated || 0);
      if (changed > 0) {
        const parts = [];
        if (result.created) parts.push(`${result.created} created`);
        if (result.updated) parts.push(`${result.updated} updated`);
        setSuccess(`Import complete: ${parts.join(', ')}.`);
        fetchMembers();
      }
    } catch (error) {
      console.error('Error importing members:', error);
      setError(error.response?.data?.detail || 'Failed to import members. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const toggleSelect = useCallback((memberId, event) => {
    if (event) event.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  // Select/deselect every member in an area (virtualized header checkbox).
  const toggleSelectAll = useCallback((groupMembers, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      groupMembers.forEach((m) => {
        if (checked) next.add(m.id);
        else next.delete(m.id);
      });
      return next;
    });
  }, []);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setIsBulkDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      const ids = Array.from(selectedIds);
      const result = await bulkDeleteMembers(ids);
      const deletedCount = result.deleted?.length ?? 0;
      const skipped = (result.not_found?.length || 0) + (result.forbidden?.length || 0);
      setSuccess(
        `Deleted ${deletedCount} member${deletedCount !== 1 ? 's' : ''}` +
        (skipped > 0 ? ` (${skipped} skipped)` : '') + '.',
      );
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      fetchMembers();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      setError(err.response?.data?.detail || 'Failed to delete selected members.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  if (isLoading && members.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const hasSearch = debouncedSearch.trim().length > 0;
  // Base cols: select + [plot on desktop] + street + name + [type on
  // desktop] + paid + share; +1 search-expand col when active.
  const baseColCount = isMobile ? 5 : 7;
  const colSpan = hasSearch ? baseColCount + 1 : baseColCount;

  return (
    <Container maxWidth="lg" disableGutters={embedded}>
      <Box sx={embedded ? {} : { my: 4 }}>
        {!embedded && (
          <Typography variant="h4" component="h1" gutterBottom>
            FPA Members
          </Typography>
        )}

        {/* Filter & Action Bar */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flexGrow: 1 }}>
            <TextField
              label="Search Members"
              variant="outlined"
              size="small"
              value={searchInput}
              onChange={handleSearchChange}
              sx={{ minWidth: { xs: '100%', sm: 220 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              options={estates}
              getOptionLabel={(option) => option.name}
              value={selectedEstate}
              onChange={(event, newValue) => {
                setSelectedEstate(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Filter by Estate" />
              )}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel id="paid-filter-label">Paid Status</InputLabel>
              <Select
                labelId="paid-filter-label"
                value={paidFilter}
                label="Paid Status"
                onChange={(e) => {
                  setPaidFilter(e.target.value);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel id="type-filter-label">Type</InputLabel>
              <Select
                labelId="type-filter-label"
                value={memberTypeFilter}
                label="Type"
                onChange={(e) => setMemberTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="member">Members</MenuItem>
                <MenuItem value="non_member">Non-members</MenuItem>
              </Select>
            </FormControl>
            {/* Link status — only with a single FPA selected (links are per-FPA). */}
            {usingBulk && (
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                <InputLabel id="link-filter-label">Link Status</InputLabel>
                <Select
                  labelId="link-filter-label"
                  value={linkStatusFilter}
                  label="Link Status"
                  onChange={(e) => setLinkStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="linked">Linked</MenuItem>
                  <MenuItem value="unlinked">Unlinked</MenuItem>
                  <MenuItem value="conflict">
                    Conflicts{conflictRefs.size > 0 ? ` (${conflictRefs.size})` : ''}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {selectedIds.size > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setBulkDeleteOpen(true)}
              >
                Delete ({selectedIds.size})
              </Button>
            )}
            {/* Import — hidden on mobile; requires import permission */}
            {canImportMembers && (
              <Button
                variant="outlined"
                startIcon={isImporting ? <CircularProgress size={18} /> : <UploadIcon />}
                onClick={handleImportClick}
                disabled={isImporting}
                sx={{ display: { xs: 'none', md: 'flex' } }}
              >
                {isImporting ? 'Importing...' : 'Import'}
              </Button>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
            />
            {/* Download — hidden on mobile; requires export permission */}
            {canExportMembers && (
            <ButtonGroup variant="outlined" disabled={isExporting} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button
                startIcon={isExporting ? <CircularProgress size={18} /> : <DownloadIcon />}
                onClick={handleDownloadData}
              >
                {isExporting ? 'Downloading...' : 'Download'}
              </Button>
              <Button
                size="small"
                onClick={handleDownloadClick}
                sx={{ px: 0.5, minWidth: 'auto' }}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            )}
            <Menu
              anchorEl={downloadAnchor}
              open={Boolean(downloadAnchor)}
              onClose={handleDownloadClose}
            >
              <MenuItem onClick={handleDownloadData}>
                <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
                Download Data
              </MenuItem>
              <MenuItem onClick={handleDownloadTemplate}>
                <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
                Download Template
              </MenuItem>
            </Menu>
            {/* Add Member — requires create permission */}
            {canCreateMember && (isMobile ? (
              <Tooltip title="Add Member">
                <IconButton
                  color="primary"
                  onClick={handleCreateMember}
                  sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateMember}
              >
                Add Member
              </Button>
            ))}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Import tenant prompt — pick the tenant the rows will be created
            under. Pre-fills + locks to the global tenant filter when one
            is selected, so most users just click "Import". */}
        <Dialog
          open={Boolean(importDialog)}
          onClose={() => !isImporting && setImportDialog(null)}
          fullScreen={isMobile}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Import members</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {importDialog?.file?.name}
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id="import-tenant-label">Tenant</InputLabel>
              <Select
                labelId="import-tenant-label"
                label="Tenant"
                value={importDialog?.tenantId || ''}
                disabled={Boolean(globalTenantId)}
                onChange={(e) =>
                  setImportDialog((prev) => prev && { ...prev, tenantId: e.target.value })
                }
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {globalTenantId && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Locked to the active tenant filter. Clear the global filter to import into a different tenant.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportDialog(null)} disabled={isImporting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleImportConfirm}
              disabled={!importDialog?.tenantId || isImporting}
            >
              {isImporting ? 'Importing...' : 'Import'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk-delete confirmation */}
        <Dialog open={bulkDeleteOpen} onClose={() => !isBulkDeleting && setBulkDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete selected members?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {selectedIds.size} member{selectedIds.size !== 1 ? 's' : ''} will be permanently deleted.
              This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDeleteOpen(false)} disabled={isBulkDeleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              startIcon={isBulkDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import result dialog */}
        <Dialog open={Boolean(importResult)} onClose={() => setImportResult(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle>Import Results</DialogTitle>
          <DialogContent>
            {importResult && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  <strong>Members created:</strong> {importResult.created}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Members updated:</strong> {importResult.updated || 0}
                </Typography>
                {(importResult.estates_created > 0 || importResult.estates_updated > 0) && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Estates:</strong> {importResult.estates_created || 0} created, {importResult.estates_updated || 0} updated
                  </Typography>
                )}
                <Typography variant="body1" gutterBottom>
                  <strong>Skipped:</strong> {importResult.skipped} (missing ref number or name)
                </Typography>
                {importResult.errors?.length > 0 && (
                  <>
                    <Typography variant="body1" color="error" gutterBottom>
                      <strong>Errors:</strong> {importResult.errors.length}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto', mt: 1 }}>
                      {importResult.errors.map((err, idx) => (
                        <Typography key={idx} variant="body2" color="error">
                          {err}
                        </Typography>
                      ))}
                    </Paper>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportResult(null)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Member count summary */}
        {!isLoading && visibleCount > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {visibleCount} member{visibleCount !== 1 ? 's' : ''} in {groupedMembers.length} area{groupedMembers.length !== 1 ? 's' : ''}
            {hasSearch && ' (filtered)'}
          </Typography>
        )}

        {isLoading && members.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Updating...</Typography>
          </Box>
        )}

        {/* Grouped member list */}
        {!isLoading && visibleCount === 0 ? (
          <Alert severity="info">No FPA members found.</Alert>
        ) : (
          <Box>
            {groupedMembers.map(group => (
              <Accordion key={group.suburb} TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold" sx={{ flexGrow: 1 }}>
                    {group.suburb}
                  </Typography>
                  <Chip
                    label={`${group.count} member${group.count !== 1 ? 's' : ''}`}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, overflowX: 'auto' }}>
                  {hasSearch ? (
                    <Table size="small" sx={{ minWidth: 400 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ width: 40 }}>
                            <Checkbox
                              size="small"
                              indeterminate={
                                group.members.some((m) => selectedIds.has(m.id))
                                && !group.members.every((m) => selectedIds.has(m.id))
                              }
                              checked={
                                group.members.length > 0
                                && group.members.every((m) => selectedIds.has(m.id))
                              }
                              onChange={(e) => toggleSelectAll(group.members, e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell padding="checkbox" sx={{ width: 40 }} />
                          {!isMobile && <TableCell>Plot #</TableCell>}
                          <TableCell>Street</TableCell>
                          <TableCell>Name</TableCell>
                          {!isMobile && <TableCell>Type</TableCell>}
                          <TableCell align="center">Paid ({currentYear})</TableCell>
                          <TableCell padding="none" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.members.map(member => (
                          <MemberRow
                            key={member.id}
                            member={member}
                            isSelected={selectedIds.has(member.id)}
                            isExpanded={Boolean(expandedMembers[member.id])}
                            hasSearch={hasSearch}
                            isMobile={isMobile}
                            matches={matchInfo[member.id]}
                            debouncedSearch={debouncedSearch}
                            colSpan={colSpan}
                            onToggleSelect={toggleSelect}
                            onToggleExpand={toggleMemberExpand}
                            onNavigate={goToMember}
                            onShare={openShare}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <VirtualizedGroupBody
                      members={group.members}
                      selectedIds={selectedIds}
                      isMobile={isMobile}
                      currentYear={currentYear}
                      onToggleSelect={toggleSelect}
                      onNavigate={goToMember}
                      onShare={openShare}
                      onToggleSelectAll={toggleSelectAll}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>

      <ShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        payload={sharePayload}
        loading={shareLoading}
        error={shareError}
      />
    </Container>
  );
};

export default FPAMemberList;
